import React, {useState, useEffect} from "react";
import { motion } from "framer-motion";
import { Video, Calendar, Phone, MapPin, Clock, Star, Award, Users, Heart, Shield, CheckCircle, MessageCircle, Mail } from "lucide-react";

const DiabetesDoctor: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const openModal2 = () => setIsModalOpen2(true);
  const closeModal2 = () => setIsModalOpen2(false);

  const slides = [
    { position: "center", class: "object-center" },
    { position: "left", class: "object-left" },
    { position: "right", class: "object-right" },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prevSlide: any) => (prevSlide + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const locations = [
    { name: "BL Kapoor Hospital, Pusa Road", days: "Monday, Tuesday, Friday & Saturday", time: "2-4pm" },
    { name: "Rainbow Children's Hospital, Malviya Nagar", days: "Wednesday & Thursday", time: "3-5pm" },
    { name: "Children's Multispeciality Clinic, Vikaspuri", days: "Saturday", time: "10am-12pm" },
    { name: "Gauri Hospital, Jawahar Nagar", days: "Tuesday & Friday", time: "11am-12pm" },
    { name: "Aartas Clinishare, Lajpat Nagar", days: "Wednesday & Friday", time: "6-7pm" },
    { name: "Apollo Cradle, Moti Nagar", days: "Monday & Thursday", time: "10am-12pm" }
  ];

  const services = [
    "Type 1 Diabetes Management",
    "Type 2 Diabetes in Children",
    "Juvenile Diabetes Care",
    "Insulin Therapy",
    "Continuous Glucose Monitoring",
    "Diabetic Ketoacidosis Treatment",
    "Growth Hormone Disorders",
    "Thyroid Disorders in Children",
    "Obesity Management",
    "PCOS in Adolescents"
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      location: "Gurgaon",
      text: "Dr. Swati Kanodia saved my daughter's life. Her diabetes management plan is excellent and very affordable.",
      rating: 5
    },
    {
      name: "Rajesh Kumar",
      location: "Delhi",
      text: "Best pediatric diabetes doctor in Delhi NCR. My son's sugar levels are now completely under control.",
      rating: 5
    },
    {
      name: "Anita Gupta",
      location: "Noida",
      text: "Very patient and understanding doctor. Explains everything clearly and provides practical solutions.",
      rating: 5
    }
  ];

  return (
    <main className="bg-white">
      {/* Hero Section */}
      <section id="landing-hero" className="relative mt-28 md:mt-48">
        <div className="relative w-full h-screen max-h-screen overflow-hidden">
          {slides.map((slide, index) => (
            <motion.img
              key={index}
              src="/images/banner.jpg"
              alt={`Best diabetes doctor in Delhi NCR ${slide.position}`}
              className={`absolute w-full h-full object-cover ${slide.class}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: index === currentSlide ? 1 : 0 }}
              transition={{ duration: 0.5 }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/40 flex flex-col justify-center items-center text-center px-4 sm:px-8 md:px-12 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-4xl mx-auto mt-24">

              <h2 className="font-work-sans text-2xl sm:text-3xl md:text-4xl text-yellow-300 mb-4 font-bold">
                Best Diabetes Doctor in Delhi NCR | Pediatric Endocrinologist
              </h2>
              <h1 className="font-fraunces-slab text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 text-white">
                Dr. Swati Kanodia
              </h1>
              <p className="text-white text-xl md:text-2xl mb-2">
                Leading Pediatric Endocrinologist & Diabetes Specialist in Delhi, Gurgaon, Noida
              </p>
              <p className="text-yellow-200 text-lg mb-6">
                ✓ 10+ Years Experience ✓ Affordable Treatment ✓ Online Consultation Available
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={openModal}
                  className="bg-purple-600 text-white font-pt-serif-regular px-8 py-4 rounded-lg flex items-center justify-center text-lg transition-all duration-300 hover:bg-purple-700 hover:scale-105 shadow-lg"
                >
                  Book Appointment
                  <Calendar className="ml-3 w-5 h-5" />
                </button>
                <button
                  onClick={openModal2}
                  className="bg-green-600 text-white font-pt-serif-regular px-8 py-4 rounded-lg flex items-center justify-center text-lg transition-all duration-300 hover:bg-green-700 hover:scale-105 shadow-lg"
                >
                  Virtual Consultation
                  <Video className="ml-3 w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-purple-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <Users className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
              <h3 className="text-2xl font-bold">5000+</h3>
              <p>Happy Patients</p>
            </div>
            <div>
              <Award className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
              <h3 className="text-2xl font-bold">10+</h3>
              <p>Years Experience</p>
            </div>
            <div>
              <MapPin className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
              <h3 className="text-2xl font-bold">6</h3>
              <p>Clinic Locations</p>
            </div>
            <div>
              <Star className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
              <h3 className="text-2xl font-bold">4.9/5</h3>
              <p>Patient Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section>
        <motion.section
          id="diabetes-doctor-delhi"
          className="bg-[#faf9f5] py-20 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
              Looking for Best Diabetes Doctor in Delhi NCR?
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <p className="text-lg md:text-xl text-gray-800 mb-6 font-pt-serif-regular leading-relaxed">
                  Finding a professional and affordable <strong>diabetes doctor in Delhi NCR</strong> can be a challenging task. 
                  Many people rely on search results and end up with overpriced treatments. 
                </p>
                
                <p className="text-lg md:text-xl text-gray-800 mb-6 font-pt-serif-regular leading-relaxed">
                  Dr. Swati Kanodia is a leading <strong>pediatric endocrinologist and diabetes specialist in Delhi</strong> who provides 
                  comprehensive and personalized diabetic care to children and adolescents across Delhi, Gurgaon, Noida, and Faridabad.
                </p>

                <div className="bg-amber-100 p-6 rounded-lg border-l-4 border-amber-500">
                  <h3 className="text-xl font-bold text-purple-900 mb-3">Why Choose Dr. Swati Kanodia?</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /> 10+ years specialized experience</li>
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /> Affordable consultation fees</li>
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /> Multiple clinic locations</li>
                    <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /> Online consultation available</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-purple-900 mb-6">Treatment Approach</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Heart className="w-6 h-6 text-red-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Personalized Diet Plans</h4>
                      <p className="text-gray-600">Customized nutrition plans for optimal blood sugar control</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Shield className="w-6 h-6 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Regular Monitoring</h4>
                      <p className="text-gray-600">Continuous glucose monitoring and regular check-ups</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Users className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Family Guidance</h4>
                      <p className="text-gray-600">Complete family education and support sessions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mb-12">
              <span className="bg-amber-300 text-gray-800 px-8 py-3 rounded-full text-lg font-semibold">
                #1 Rated Diabetes Doctor in Delhi NCR
              </span>
            </div>
          </div>
        </motion.section>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
            Diabetes & Endocrine Services in Delhi NCR
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{service}</h3>
                <p className="text-gray-600 text-sm">Expert care and management for optimal health outcomes</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Clinic Locations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
            Clinic Locations Across Delhi NCR
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <MapPin className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{location.name}</h3>
                <p className="text-gray-600 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  {location.days}
                </p>
                <p className="text-purple-600 font-semibold">{location.time}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-gray-800 mb-4">
              <strong>Consultation Hours:</strong> Monday-Saturday, 11am-7pm
            </p>
            <p className="text-md text-gray-600">
              Sunday consultations available for emergencies (additional charges may apply)
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-fraunces-slab text-center mb-12">
            Why Dr. Swati Kanodia is the Top Choice in Delhi NCR?
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-lg mb-6 leading-relaxed">
                Among the numerous diabetes doctors in Delhi NCR, Dr. Swati Kanodia stands out as an inspiration 
                for fellow medical professionals. Her approach to diabetes management combines:
              </p>
              
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-300 mb-2">1. Affordability</h4>
                  <p className="text-gray-200">Reasonable consultation fees with transparent pricing</p>
                </div>
                <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-300 mb-2">2. Extensive Experience</h4>
                  <p className="text-gray-200">10+ years of specialized pediatric endocrinology practice</p>
                </div>
                <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-300 mb-2">3. Time Flexibility</h4>
                  <p className="text-gray-200">Multiple time slots across 6 convenient locations</p>
                </div>
                <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-300 mb-2">4. Online Consultation</h4>
                  <p className="text-gray-200">Virtual consultations for follow-ups and emergencies</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-yellow-300">Patient Success Stories</h3>
              <div className="space-y-6">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="border-b border-white/20 pb-4 last:border-b-0">
                    <div className="flex mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current text-yellow-300" />
                      ))}
                    </div>
                    <p className="text-gray-200 mb-2">"{testimonial.text}"</p>
                    <p className="text-yellow-300 font-semibold">
                      - {testimonial.name}, {testimonial.location}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <span className="bg-amber-300 text-gray-800 px-8 py-3 rounded-full text-lg font-semibold">
              Most Trusted Diabetes Specialist in Delhi NCR
            </span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
            Contact Dr. Swati Kanodia - Diabetes Doctor Delhi NCR
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-purple-900 mb-6">Get in Touch</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <Phone className="w-6 h-6 text-green-600 mr-4" />
                  <div>
                    <p className="font-semibold">Call/WhatsApp for Appointment</p>
                    <p className="text-gray-600">Available Monday-Saturday, 11am-7pm</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <MessageCircle className="w-6 h-6 text-blue-600 mr-4" />
                  <div>
                    <p className="font-semibold">Emergency Consultation</p>
                    <p className="text-gray-600">Sunday emergency consultations available</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Mail className="w-6 h-6 text-purple-600 mr-4" />
                  <div>
                    <p className="font-semibold">Online Consultation</p>
                    <p className="text-gray-600">Virtual appointments available</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-amber-50 rounded-lg border-l-4 border-amber-500">
                <h4 className="font-semibold text-amber-800 mb-2">Quick Appointment Booking</h4>
                <p className="text-amber-700 text-sm">
                  Call or message for same-day appointments. Emergency consultations available 24/7.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-purple-900 mb-6">Why Choose Us?</h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Comprehensive Diabetes Care</h4>
                    <p className="text-gray-600 text-sm">From diagnosis to long-term management</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Latest Treatment Methods</h4>
                    <p className="text-gray-600 text-sm">Modern insulin therapy and monitoring techniques</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Family-Centered Approach</h4>
                    <p className="text-gray-600 text-sm">Education and support for the entire family</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Affordable Healthcare</h4>
                    <p className="text-gray-600 text-sm">Quality treatment at reasonable prices</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openModal}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-purple-700"
                >
                  Book Appointment
                  <Calendar className="ml-2 w-5 h-5" />
                </button>
                <button
                  onClick={openModal2}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-green-700"
                >
                  Virtual Consultation
                  <Video className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl text-purple-900 font-fraunces-slab text-center mb-12">
            Frequently Asked Questions - Diabetes Treatment Delhi NCR
          </h2>
          
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                What makes Dr. Swati Kanodia the best diabetes doctor in Delhi NCR?
              </h3>
              <p className="text-gray-600">
                Dr. Kanodia combines 10+ years of specialized experience with affordable treatment options, 
                multiple convenient locations, and a personalized approach to diabetes management for children and adolescents.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Are online consultations available for diabetes treatment?
              </h3>
              <p className="text-gray-600">
                Yes, Dr. Kanodia offers virtual consultations for follow-up appointments and emergency consultations, 
                making diabetes care accessible from anywhere in Delhi NCR.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                What types of diabetes does Dr. Kanodia treat?
              </h3>
              <p className="text-gray-600">
                Dr. Kanodia specializes in treating Type 1 diabetes, Type 2 diabetes in children, juvenile diabetes, 
                and other endocrine disorders including thyroid issues and growth hormone problems.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                How can I book an appointment with Dr. Swati Kanodia?
              </h3>
              <p className="text-gray-600">
                You can book appointments by calling or messaging during consultation hours (Monday-Saturday, 11am-7pm). 
                Emergency consultations are available on Sundays with additional charges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-900 to-blue-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get the Best Diabetes Care in Delhi NCR?
          </h2>
          <p className="text-xl mb-8">
            Book your consultation with Dr. Swati Kanodia today and take the first step towards better diabetes management.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={openModal}
              className="bg-white text-purple-900 font-semibold px-8 py-4 rounded-lg flex items-center justify-center text-lg transition-all duration-300 hover:bg-gray-100"
            >
              Book In-Person Appointment
              <Calendar className="ml-3 w-5 h-5" />
            </button>
            <button
              onClick={openModal2}
              className="bg-green-600 text-white font-semibold px-8 py-4 rounded-lg flex items-center justify-center text-lg transition-all duration-300 hover:bg-green-700"
            >
              Start Virtual Consultation
              <Video className="ml-3 w-5 h-5" />
            </button>
          </div>
          
          <p className="mt-6 text-lg">
            📞 Call now for same-day appointments | 🏥 6 convenient locations across Delhi NCR
          </p>
        </div>
      </section>

      {/* Modal Components (you'll need to implement these) */}
      {/* <OfflineModal isOpen={isModalOpen} onClose={closeModal} />
      <AppointmentModal isOpen={isModalOpen2} onClose={closeModal2} /> */}
    </main>
  );
};

export default DiabetesDoctor;